import _debounce from "lodash/debounce";
const _excluded = ["anchorEl", "children", "className", "container", "modalClasses", "onEnter", "onEntered", "onExit", "onExited", "onExiting", "open", "role", "supportsMobile", "TransitionComponent", "transitionDuration", "TransitionProps"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import warning from 'warning';
import Modal from '../Modal';
import { Grow, Slide } from '../Transitions';
import { isWindowDown } from '../utils/responsive';
import ownerDocument from '../utils/ownerDocument';
import ownerWindow from '../utils/ownerWindow';
import { jsx as ___EmotionJSX } from "@emotion/react";
function getOffsetTop(rect, y) {
  let offset = 0;
  if (typeof y === 'number') {
    offset = y;
  } else if (y === 'center') {
    offset = rect.height / 2;
  } else if (y === 'bottom') {
    offset = rect.height;
  }
  return offset;
}
function getOffsetLeft(rect, x) {
  let offset = 0;
  if (typeof x === 'number') {
    offset = x;
  } else if (x === 'center') {
    offset = rect.width / 2;
  } else if (x === 'right') {
    offset = rect.width;
  }
  return offset;
}
function getTransformOriginValue(transformOrigin) {
  return [transformOrigin.x, transformOrigin.y].map(n => typeof n === 'number' ? `${n}px` : n).join(' ');
}
function getScrollParent(parent, child) {
  let element = child;
  let scrollTop = 0;
  while (element && element !== parent) {
    element = element.parentNode;
    scrollTop += element.scrollTop;
  }
  return scrollTop;
}
function getAnchorEl(anchorEl) {
  return typeof anchorEl === 'function' ? anchorEl() : anchorEl;
}
const containerStyles = {
  position: 'absolute',
  maxWidth: 'calc(100% - 32px)',
  maxHeight: 'calc(100% - 32px)',
  outline: 'none'
};
const mobileContainerStyles = {
  position: 'absolute',
  maxWidth: '100%',
  maxHeight: '100%',
  outline: 'none',
  bottom: 0,
  width: '100%'
};
class Popover extends Component {
  constructor() {
    super();
    _defineProperty(this, "handleGetOffsetTop", getOffsetTop);
    _defineProperty(this, "handleGetOffsetLeft", getOffsetLeft);
    _defineProperty(this, "state", {
      style: {}
    });
    _defineProperty(this, "setPositioningStyles", element => {
      const newStyle = this.getPositioningStyle(element);
      if (newStyle.top) this.setState({
        style: newStyle
      });
    });
    _defineProperty(this, "getPositioningStyle", element => {
      const {
        anchorEl,
        anchorReference,
        marginThreshold,
        supportsMobile
      } = this.props;
      const contentAnchorOffset = this.getContentAnchorOffset(element);
      const elemRect = {
        width: element.offsetWidth,
        height: element.offsetHeight
      };
      const transformOrigin = this.getTransformOrigin(elemRect, contentAnchorOffset);
      const isMobile = isWindowDown('xs') && supportsMobile;
      if (anchorReference === 'none') {
        return {
          top: null,
          left: null,
          transformOrigin: getTransformOriginValue(transformOrigin)
        };
      }
      if (isMobile) {
        return {
          top: null,
          bottom: 0,
          left: 0,
          transformOrigin: getTransformOriginValue(transformOrigin)
        };
      }
      const anchorOffset = this.getAnchorOffset(contentAnchorOffset);
      let top = anchorOffset.top - transformOrigin.y;
      let left = anchorOffset.left - transformOrigin.x;
      const bottom = top + elemRect.height;
      const right = left + elemRect.width;
      const containerWindow = ownerWindow(getAnchorEl(anchorEl));
      const heightThreshold = containerWindow.innerHeight - marginThreshold;
      const widthThreshold = containerWindow.innerWidth - marginThreshold;
      if (top < marginThreshold) {
        const diff = top - marginThreshold;
        top -= diff;
        transformOrigin.y += diff;
      } else if (bottom > heightThreshold) {
        const diff = bottom - heightThreshold;
        top -= diff;
        transformOrigin.y += diff;
      }
      warning(element.height < heightThreshold || !element.height || !heightThreshold, ['The Popover component is too tall.', `Some part of it can not be seen on the screen (${element.height = heightThreshold}px).)`, 'Please consider adding a `max-height` to improve the user-experience.'].join('\n'));
      if (left < marginThreshold) {
        const diff = left - marginThreshold;
        left -= diff;
        transformOrigin.x += diff;
      } else if (right > widthThreshold) {
        const diff = right - widthThreshold;
        left -= diff;
        transformOrigin.x += diff;
      }
      const elemStyles = {
        top: `${top}px`,
        left: `${left}px`,
        transformOrigin: getTransformOriginValue(transformOrigin)
      };
      return elemStyles;
    });
    _defineProperty(this, "handleEntering", element => {
      if (this.props.onEntering) this.props.onEntering(element);
      this.setPositioningStyles(element);
    });
    if (typeof window !== 'undefined') {
      this.handleResize = _debounce(() => {
        this.setPositioningStyles(this.containerRef);
      }, 166); // Corresponds to 10 frames at 60 Hz.
    }
  }
  componentDidUpdate(prevProps) {
    if (prevProps.anchorOrigin.x !== this.props.anchorOrigin.x || prevProps.anchorOrigin.y !== this.props.anchorOrigin.y || prevProps.transformOrigin.x !== this.props.transformOrigin.x || prevProps.transformOrigin.y !== this.props.transformOrigin.y) {
      this.handleResize();
    }
  }
  componentDidMount() {
    if (this.props.action) {
      this.props.action({
        updatePosition: this.handleResize
      });
    }
    window.addEventListener('resize', this.handleResize);
  }
  componentWillUnmount() {
    if (this.handleResize && this.handleResize.clear) {
      this.handleResize.clear();
    }
    window.removeEventListener('resize', this.handleResize);
  }
  getAnchorOffset(contentAnchorOffset) {
    const {
      anchorEl,
      anchorOrigin,
      anchorReference,
      anchorPosition
    } = this.props;
    if (anchorReference === 'anchorPosition') {
      warning(anchorPosition, 'You need to provide a `anchorPosition` property when using <Popover anchorReference="anchorPosition" />');
      return anchorPosition;
    }
    const anchorElement = getAnchorEl(anchorEl) || ownerDocument(this.containerRef).body;
    const anchorRect = anchorElement.getBoundingClientRect();
    const anchorY = contentAnchorOffset === 0 ? anchorOrigin.y : 'center';
    return {
      top: anchorRect.top + this.handleGetOffsetTop(anchorRect, anchorY),
      left: anchorRect.left + this.handleGetOffsetLeft(anchorRect, anchorOrigin.x)
    };
  }
  getContentAnchorOffset(element) {
    const {
      getContentAnchorEl,
      anchorReference
    } = this.props;
    let contentAnchorOffset = 0;
    if (getContentAnchorEl && anchorReference === 'anchorEl') {
      const contentAnchorEl = getContentAnchorEl(element);
      if (contentAnchorEl && element.contains(contentAnchorEl)) {
        const scrollTop = getScrollParent(element, contentAnchorEl);
        contentAnchorOffset = contentAnchorEl.offsetTop + contentAnchorEl.clientHeight / 2 - scrollTop || 0;
      }
    }
    return contentAnchorOffset;
  }
  getTransformOrigin(elemRect, contentAnchorOffset = 0) {
    const {
      transformOrigin,
      supportsMobile
    } = this.props;
    const isMobile = isWindowDown('xs') && supportsMobile;
    if (isMobile) {
      return {
        x: 'center',
        y: 'bottom'
      };
    }
    return {
      y: this.handleGetOffsetTop(elemRect, transformOrigin.y) + contentAnchorOffset,
      x: this.handleGetOffsetLeft(elemRect, transformOrigin.x)
    };
  }
  render() {
    const _this$props = this.props,
      {
        anchorEl,
        children,
        className,
        container: containerProp,
        modalClasses,
        onEnter,
        onEntered,
        onExit,
        onExited,
        onExiting,
        open,
        role,
        supportsMobile,
        TransitionComponent,
        transitionDuration: transitionDurationProp,
        TransitionProps
      } = _this$props,
      other = _objectWithoutProperties(_this$props, _excluded);
    const {
      style
    } = this.state;
    const transitionDuration = transitionDurationProp;
    const container = containerProp || (anchorEl ? ownerDocument(getAnchorEl(anchorEl)).body : undefined);
    const isMobile = isWindowDown('xs') && supportsMobile;
    const PopoverTransitionComponent = isMobile ? Slide : TransitionComponent;
    const PopoverTransitionProps = isMobile ? {
      direction: 'up'
    } : TransitionProps;
    return ___EmotionJSX(Modal, _extends({
      classes: modalClasses,
      className: className,
      container: container,
      open: open,
      BackdropProps: {
        invisible: !isMobile
      }
    }, other), ___EmotionJSX(PopoverTransitionComponent, _extends({
      appear: true,
      in: open,
      onEnter: onEnter,
      onEntered: onEntered,
      onEntering: this.handleEntering,
      onExit: onExit,
      onExiting: onExiting,
      onExited: onExited,
      role: role,
      style: style,
      timeout: transitionDuration
    }, PopoverTransitionProps), ___EmotionJSX("div", {
      style: isMobile ? mobileContainerStyles : containerStyles,
      ref: ref => {
        // eslint-disable-next-line react/no-find-dom-node
        this.containerRef = ReactDOM.findDOMNode(ref);
      }
    }, children)));
  }
}
Popover.propTypes = {
  action: PropTypes.func,
  anchorEl: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  anchorOrigin: PropTypes.shape({
    x: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['left', 'center', 'right'])]).isRequired,
    y: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['top', 'center', 'bottom'])]).isRequired
  }),
  anchorPosition: PropTypes.shape({
    left: PropTypes.number.isRequired,
    top: PropTypes.number.isRequired
  }),
  anchorReference: PropTypes.oneOf(['anchorEl', 'anchorPosition', 'none']),
  children: PropTypes.node,
  container: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  elevation: PropTypes.number,
  getContentAnchorEl: PropTypes.func,
  marginThreshold: PropTypes.number,
  ModalClasses: PropTypes.object,
  onClose: PropTypes.func,
  onEnter: PropTypes.func,
  onEntered: PropTypes.func,
  onEntering: PropTypes.func,
  onExit: PropTypes.func,
  onExited: PropTypes.func,
  onExiting: PropTypes.func,
  open: PropTypes.bool.isRequired,
  PaperProps: PropTypes.object,
  role: PropTypes.string,
  transformOrigin: PropTypes.shape({
    x: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['left', 'center', 'right'])]).isRequired,
    y: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['top', 'center', 'bottom'])]).isRequired
  }),
  TransitionComponent: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  transitionDuration: PropTypes.oneOfType([PropTypes.number, PropTypes.shape({
    enter: PropTypes.number,
    exit: PropTypes.number
  }), PropTypes.oneOf(['auto'])]),
  TransitionProps: PropTypes.object
};
Popover.defaultProps = {
  anchorReference: 'anchorEl',
  anchorOrigin: {
    y: 'top',
    x: 'left'
  },
  elevation: 8,
  marginThreshold: 16,
  transformOrigin: {
    y: 'top',
    x: 'left'
  },
  TransitionComponent: Grow,
  transitionDuration: 200
};
export default Popover;